import {z} from 'zod';

// Enums

export const ClosingDayTypeSchema = z.enum([
  'Public',
  'Bank',
  'School',
  'Authorities',
  'Optional',
  'Observance',
  'Custom',
]);

export const ClosingDaySourceSchema = z.enum(['nager_date', 'manual']);

// Request/Response Body Schemas

export const CreateCustomClosingDayRequestBodySchema = z.object({
  date: z.string(),
  name: z.string().min(1),
  branchIds: z.array(z.string().uuid()).nullable(),
  recurring: z.boolean(),
});

export const UpdateCustomClosingDayRequestBodySchema = z.object({
  date: z.string(),
  name: z.string().min(1),
  branchIds: z.array(z.string().uuid()).nullable(),
  recurring: z.boolean(),
});

export const UpdatePublicClosingDayRequestBodySchema = z.object({
  branchIds: z.array(z.string().uuid()).nullable(),
  enabled: z.boolean(),
});

const CLOSING_DAY_SYNC_MIN_YEAR = 2025;
const CLOSING_DAY_SYNC_MAX_YEAR = 2100;

export const SyncPublicClosingDaysRequestBodySchema = z.object({
  year: z.number().min(CLOSING_DAY_SYNC_MIN_YEAR).max(CLOSING_DAY_SYNC_MAX_YEAR),
});

export const CreateCustomClosingDayResponseBodySchema = z.object({
  id: z.string(),
});

export const GetClosingDayResponseBodySchema = z.object({
  id: z.string(),
  date: z.string(),
  localName: z.string(),
  englishName: z.string(),
  countryCode: z.string(),
  global: z.boolean(),
  counties: z.array(z.string()).nullable(),
  types: z.array(ClosingDayTypeSchema),
  source: ClosingDaySourceSchema,
  enabled: z.boolean(),
  branchIds: z.array(z.string()),
  recurring: z.boolean(),
});

// API Response Schemas

export const GetClosingDayApiResponseSchema = GetClosingDayResponseBodySchema; // status 200
export const CreateCustomClosingDayApiResponseSchema = CreateCustomClosingDayResponseBodySchema; // status 201
export const UpdateCustomClosingDayApiResponseSchema = z.void(); // status 204
export const DeleteCustomClosingDayApiResponseSchema = z.void(); // status 204
export const SyncPublicClosingDaysApiResponseSchema = z.void(); // status 204
export const UpdatePublicClosingDayApiResponseSchema = z.void(); // status 204

// API Arguments Schemas

export const GetClosingDayApiArgSchema = z.object({
  closingDayId: z.string(),
});

export const CreateCustomClosingDayApiArgSchema = CreateCustomClosingDayRequestBodySchema;

export const UpdateCustomClosingDayApiArgSchema = z
  .object({closingDayId: z.string()})
  .merge(UpdateCustomClosingDayRequestBodySchema);

export const DeleteCustomClosingDayApiArgSchema = z.object({
  closingDayId: z.string(),
});

export const SyncPublicClosingDaysApiArgSchema = SyncPublicClosingDaysRequestBodySchema;

export const UpdatePublicClosingDayApiArgSchema = z
  .object({closingDayId: z.string()})
  .merge(UpdatePublicClosingDayRequestBodySchema);

// Exported Types (Inferred)

export type ClosingDayType = z.infer<typeof ClosingDayTypeSchema>;
export type ClosingDaySource = z.infer<typeof ClosingDaySourceSchema>;
export type CreateCustomClosingDayRequestBody = z.infer<
  typeof CreateCustomClosingDayRequestBodySchema
>;
export type UpdateCustomClosingDayRequestBody = z.infer<
  typeof UpdateCustomClosingDayRequestBodySchema
>;
export type UpdatePublicClosingDayRequestBody = z.infer<
  typeof UpdatePublicClosingDayRequestBodySchema
>;
export type SyncPublicClosingDaysRequestBody = z.infer<
  typeof SyncPublicClosingDaysRequestBodySchema
>;
export type CreateCustomClosingDayResponseBody = z.infer<
  typeof CreateCustomClosingDayResponseBodySchema
>;
export type GetClosingDayResponseBody = z.infer<typeof GetClosingDayResponseBodySchema>;

// API Response Types

export type GetClosingDayApiResponse = z.infer<typeof GetClosingDayApiResponseSchema>;
export type CreateCustomClosingDayApiResponse = z.infer<
  typeof CreateCustomClosingDayApiResponseSchema
>;
export type UpdateCustomClosingDayApiResponse = z.infer<
  typeof UpdateCustomClosingDayApiResponseSchema
>;
export type DeleteCustomClosingDayApiResponse = z.infer<
  typeof DeleteCustomClosingDayApiResponseSchema
>;
export type SyncPublicClosingDaysApiResponse = z.infer<
  typeof SyncPublicClosingDaysApiResponseSchema
>;
export type UpdatePublicClosingDayApiResponse = z.infer<
  typeof UpdatePublicClosingDayApiResponseSchema
>;

// API Argument Types

export type GetClosingDayApiArg = z.infer<typeof GetClosingDayApiArgSchema>;
export type CreateCustomClosingDayApiArg = z.infer<typeof CreateCustomClosingDayApiArgSchema>;
export type UpdateCustomClosingDayApiArg = z.infer<typeof UpdateCustomClosingDayApiArgSchema>;
export type DeleteCustomClosingDayApiArg = z.infer<typeof DeleteCustomClosingDayApiArgSchema>;
export type SyncPublicClosingDaysApiArg = z.infer<typeof SyncPublicClosingDaysApiArgSchema>;
export type UpdatePublicClosingDayApiArg = z.infer<typeof UpdatePublicClosingDayApiArgSchema>;
